<template>
    <div class="qr-page">
        <div class="qr-header">
            <img src="@/assets/logo_site_white.webp" alt="Black Pizza" class="check-header-logo">
            <div class="qr-header-sub">Ваше замовлення</div>
        </div>

        <div v-if="loading" class="qr-loading">
            <img src="@/assets/loading.svg" alt="Завантаження" style="width: 34px;">
        </div>

        <div v-else-if="invalid" class="check-block">
            <div class="check-invalid-title">Посилання недійсне</div>
            <div class="check-invalid-hint">Перевірте QR на чеку або перейдіть на сайт</div>
            <div class="check-actions">
                <button class="qr-btn qr-btn-outline" @click="goHome">На сайт</button>
                <button class="qr-btn" @click="openApp">Завантажити додаток</button>
            </div>
        </div>

        <div v-else-if="noOrder" class="check-block">
            <div class="check-invalid-hint">Відскануйте QR на чеку або введіть номер замовлення</div>
            <form class="check-lookup" @submit.prevent="lookup">
                <input v-model.trim="lookupOrder" class="check-input" type="text" inputmode="numeric" placeholder="Номер замовлення" autocomplete="off">
                <div v-if="lookupError" class="qr-error">{{ lookupError }}</div>
                <button class="qr-btn" type="submit" :disabled="lookingUp">Побачити замовлення</button>
            </form>
            <div class="check-actions">
                <button class="qr-btn qr-btn-outline" @click="goHome">На сайт</button>
                <button class="qr-btn" @click="openApp">Завантажити додаток</button>
            </div>
        </div>

        <div v-else-if="paidReturn && order.is_paid" class="check-block qr-success">
            <div class="qr-success-check">✓</div>
            <div class="qr-success-title">Оплачено</div>
            <div class="qr-success-comment">{{ order.sums.payable }} грн</div>
            <div class="check-time">{{ formattedDate }}</div>
            <div class="qr-success-hint">{{ order.type === 'delivery' ? 'Покажіть цей екран кур\'єру' : 'Покажіть цей екран на видачі' }}</div>
            <div class="check-actions">
                <button class="qr-btn qr-btn-outline" @click="goHome">На сайт</button>
                <button class="qr-btn" @click="openApp">Завантажити додаток</button>
            </div>
        </div>

        <div v-else-if="paidReturn && pollTimedOut" class="check-block">
            <div class="check-invalid-title">Оплата ще обробляється</div>
            <div class="check-invalid-hint">Оновіть сторінку за хвилину</div>
            <div class="check-actions">
                <button class="qr-btn qr-btn-outline" @click="goHome">На сайт</button>
                <button class="qr-btn" @click="openApp">Завантажити додаток</button>
            </div>
        </div>

        <div v-else-if="paidReturn" class="check-block">
            <div class="qr-loading">
                <img src="@/assets/loading.svg" alt="Перевіряємо оплату" style="width: 34px;">
            </div>
            <div class="check-invalid-hint check-center">Перевіряємо оплату…</div>
        </div>

        <div v-else class="check-block">
            <div class="check-card">
                <div class="check-card-row check-card-top">
                    <div class="check-order-title">Замовлення №{{ order.id }}</div>
                    <div class="check-badge" :class="badgeClass">{{ badgeText }}</div>
                </div>
                <div class="check-accepted">Прийнято {{ formattedDate }}</div>

                <div class="check-items">
                    <div v-for="(item, index) in order.items" :key="index" class="check-item">
                        {{ item.title }}<template v-if="item.size"> ({{ item.size }})</template> ×{{ item.quanity }}
                        <div v-if="item.ingredients && item.ingredients.length" class="check-item-addons">
                            <div v-for="(addon, addonIndex) in item.ingredients" :key="addonIndex">
                                + {{ addon.title }}<template v-if="addon.quanity > 1"> ×{{ addon.quanity }}</template>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="check-sums">
                    <div class="check-sum-row">
                        <span>Сума</span>
                        <span>{{ order.sums.total }} грн</span>
                    </div>
                    <div v-if="order.sums.discount > 0" class="check-sum-row">
                        <span>Знижка</span>
                        <span>−{{ order.sums.discount }} грн</span>
                    </div>
                    <div v-if="order.sums.delivery > 0" class="check-sum-row">
                        <span>Доставка</span>
                        <span>{{ order.sums.delivery }} грн</span>
                    </div>
                    <div class="check-sum-row check-sum-total">
                        <span>До сплати</span>
                        <span>{{ order.sums.payable }} грн</span>
                    </div>
                </div>

                <div v-if="cashHint" class="check-hint">{{ order.type === 'delivery' ? 'Оплата онлайн недоступна — розрахуйтесь із кур\'єром' : 'Оплата онлайн недоступна — розрахуйтесь на видачі' }}</div>
                <div v-if="payError" class="qr-error">{{ payError }}</div>

                <button v-if="order.can_pay" class="qr-btn" :disabled="paying" @click="pay">
                    {{ paying ? 'Переходимо до оплати…' : 'Оплатити онлайн ' + order.sums.payable + ' грн' }}
                </button>
            </div>

            <div class="check-actions">
                <button class="qr-btn qr-btn-outline" @click="goHome">На сайт</button>
                <button class="qr-btn qr-btn-outline" @click="openApp">Завантажити додаток</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'

// Хардкод адреси LiqPay: сторінка ніколи не переходить за URL, отриманим з API
const APP_STORE_URL = 'https://apps.apple.com/cy/app/black-pizza/id1498371417'
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.oakmont.blackpizza'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 30000

export default {
    name: 'check',

    data() {
        return {
            loading: true,
            invalid: false,
            order: null,
            paying: false,
            payError: '',
            paidReturn: false,
            pollTimedOut: false,
            pollTimer: null,
            lookupOrder: '',
            lookupError: '',
            lookingUp: false,
        }
    },

    computed: {
        noOrder() {
            return !this.$route.params.id || !this.$route.params.sig
        },

        formattedDate() {
            if (!this.order || !this.order.created_at) {
                return ''
            }

            const date = new Date(this.order.created_at)
            if (isNaN(date.getTime())) {
                return ''
            }

            const parts = new Intl.DateTimeFormat('uk-UA', {
                timeZone: 'Europe/Kyiv',
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }).formatToParts(date)

            const get = type => (parts.find(p => p.type === type) || {}).value || ''

            return `${get('day')}.${get('month')} ${get('hour')}:${get('minute')}`
        },

        badgeClass() {
            if (this.order.is_paid) return 'check-badge-paid'
            if (this.order.status_id === 4) return 'check-badge-cancelled'
            if (this.order.can_pay) return 'check-badge-waiting'
            return 'check-badge-processing'
        },

        badgeText() {
            if (this.order.is_paid) return 'Оплачено'
            if (this.order.status_id === 4) return 'Скасовано'
            if (this.order.can_pay) return 'Очікує оплати'
            // Виконане чи відправлене замовлення — назва статусу з API («Виконано», «Відправлено»…)
            return this.order.status_title || 'В обробці'
        },

        cashHint() {
            return !this.order.is_paid
                && !this.order.can_pay
                && this.order.status_id !== 4
                && (this.order.payment_type === 'Готівка' || this.order.payment_type === 'Картка')
        },
    },

    created() {
        this.referrerMeta = document.createElement('meta')
        this.referrerMeta.setAttribute('name', 'referrer')
        this.referrerMeta.setAttribute('content', 'no-referrer')
        document.head.appendChild(this.referrerMeta)

        if (this.noOrder) {
            this.loading = false
            return
        }

        this.paidReturn = this.$route.query.paid === '1'
        this.load()
    },

    beforeDestroy() {
        if (this.referrerMeta && this.referrerMeta.parentNode) {
            this.referrerMeta.parentNode.removeChild(this.referrerMeta)
        }
        this.stopPolling()
    },

    methods: {
        checkUrl() {
            return `${Vue.prototype.SERVER_URL}/api/check/${this.$route.params.id}/${this.$route.params.sig}`
        },

        load() {
            axios.get(this.checkUrl()).then(response => {
                this.order = response.data
                this.loading = false

                if (this.paidReturn && !this.order.is_paid) {
                    this.startPolling()
                }
            }).catch(() => {
                this.invalid = true
                this.loading = false
            })
        },

        startPolling() {
            const startedAt = Date.now()

            this.pollTimer = setInterval(() => {
                if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
                    this.stopPolling()
                    this.pollTimedOut = true
                    return
                }

                axios.get(this.checkUrl()).then(response => {
                    this.order = response.data
                    if (this.order.is_paid) {
                        this.stopPolling()
                    }
                }).catch(() => { /* не зупиняємо опитування через тимчасову помилку мережі */ })
            }, POLL_INTERVAL_MS)
        },

        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
            }
        },

        pay() {
            this.paying = true
            this.payError = ''

            axios.post(`${this.checkUrl()}/pay`, {}).then(() => {
                this.openLiqpayPage()
            }).catch(e => {
                this.paying = false
                const status = e.response && e.response.status

                if (status === 409) {
                    this.payError = 'Оплата зараз недоступна для цього замовлення'
                } else if (status === 429) {
                    this.payError = 'Забагато спроб. Спробуйте трохи пізніше'
                } else if (status === 503) {
                    this.payError = this.order.type === 'delivery'
                        ? 'Оплата тимчасово недоступна. Розрахуйтесь із кур\'єром'
                        : 'Оплата тимчасово недоступна. Розрахуйтесь на видачі'
                } else {
                    this.payError = 'Не вдалося почати оплату. Спробуйте ще раз'
                }
            })
        },

        // Оплата йде через ту саму сторінку з віджетом LiqPay, що й на сайті
        // (/payment/liqpay/{id}); POST /pay перед цим оновив дані оплати в замовленні.
        // Пряма форма на api/3/checkout 04.09.2026 не давала Apple Pay і Google Pay
        // створити платіж, а віджет ті самі оплати проводив. Адреса складається лише
        // з константи сервера і номера замовлення.
        openLiqpayPage() {
            window.location.assign(`${Vue.prototype.SERVER_URL}/payment/liqpay/${encodeURIComponent(String(this.order.id))}`)
        },

        // Пошук без QR лише за номером (рішення власника 04.09.2026) → API віддає id і
        // підпис, адресу збираємо самі й лише з перевірених частин (цифри та 16 hex).
        lookup() {
            const id = String(this.lookupOrder).replace(/\D+/g, '')

            if (!id) {
                this.lookupError = 'Вкажіть номер замовлення'
                return
            }

            this.lookingUp = true
            this.lookupError = ''

            axios.post(`${Vue.prototype.SERVER_URL}/api/check/lookup`, { order: id }).then(response => {
                const orderId = String(response.data.id || '')
                const sig = String(response.data.sig || '')

                if (!/^\d+$/.test(orderId) || !/^[0-9a-f]{16}$/.test(sig)) {
                    throw new Error('bad lookup response')
                }

                window.location.assign(`/check/${orderId}/${sig}`)
            }).catch(e => {
                const status = e.response && e.response.status
                this.lookupError = status === 429
                    ? 'Забагато спроб. Спробуйте трохи пізніше'
                    : 'Замовлення з таким номером не знайдено'
                this.lookingUp = false
            })
        },

        goHome() {
            this.$router.push('/')
        },

        openApp() {
            const ua = navigator.userAgent
            let url = APP_STORE_URL

            if (ua.match(/Android/i)) {
                url = GOOGLE_PLAY_URL
            } else if (!(ua.match(/iPhone/i) || ua.match(/iPad/i))) {
                // невизначений пристрій — ведемо на App Store, кнопка одна на десктопі теж
                url = APP_STORE_URL
            }

            const win = window.open(url, '_blank', 'noopener')
            if (win) {
                win.opener = null
            }
        },
    },
}
</script>

<style scoped>
.qr-page {
    max-width: 480px;
    min-height: 100vh;
    margin: 0 auto;

    text-align: left;
    background: #fff;
    color: #1a1a1a;
}

.qr-header {
    padding: 18px 16px 14px;
    background: #000;
    color: #fff;
    text-align: center;
}

.qr-header-logo {
    font-size: 1.5rem;
    font-weight: 900;
    letter-spacing: 2px;
}

.qr-header-sub {
    margin-top: 2px;
    font-size: 0.9rem;
    opacity: 0.75;
}

.qr-loading {
    padding: 60px 0;
    text-align: center;
}

.check-block {
    padding: 20px 16px 32px;
}

.check-invalid-title {
    margin-top: 40px;
    font-size: 1.3rem;
    font-weight: 900;
    text-align: center;
}

.check-invalid-hint {
    margin-top: 8px;
    color: #888;
    font-size: 0.9rem;
    text-align: center;
}

.check-center {
    margin-top: 14px;
}

.check-card {
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    background: #fafafa;
}

.check-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.check-order-title {
    font-size: 1.2rem;
    font-weight: 900;
}

.check-badge {
    padding: 5px 10px;
    border-radius: 20px;

    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
}

.check-badge-paid {
    color: #1e7a34;
    background: rgba(46, 158, 68, 0.15);
}

.check-badge-cancelled {
    color: #888;
    background: rgba(0, 0, 0, 0.06);
}

.check-badge-waiting {
    color: #a15c00;
    background: rgba(255, 170, 0, 0.15);
}

.check-badge-processing {
    color: #555;
    background: rgba(0, 0, 0, 0.06);
}

.check-accepted {
    margin-top: 4px;
    color: #888;
    font-size: 0.85rem;
}

.check-items {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.check-lookup {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 14px 0;
}

.check-input {
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 1rem;
    background: #fff;
    color: #111;
}

.check-header-logo {
    display: block;
    width: 210px;
    max-width: 70%;
    margin: 0 auto 6px;
}

.check-item-addons {
    margin: 2px 0 0 14px;
    font-size: 0.92em;
    opacity: 0.85;
}

.check-item {
    padding: 4px 0;
    font-size: 0.95rem;
}

.check-sums {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.check-sum-row {
    display: flex;
    justify-content: space-between;

    padding: 3px 0;
    font-size: 0.9rem;
    color: #555;
}

.check-sum-total {
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    color: #1a1a1a;
    font-size: 1.1rem;
    font-weight: 900;
}

.check-hint {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 8px;

    color: #a15c00;
    font-size: 0.85rem;
    font-weight: 600;

    background: rgba(255, 170, 0, 0.1);
}

.check-time {
    margin-top: 10px;
    color: #888;
    font-size: 0.9rem;
}

.check-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 18px;
}

.qr-error {
    margin-top: 10px;
    color: #AB2321;
    font-size: 0.85rem;
    font-weight: 600;
}

.qr-btn {
    width: 100%;

    margin-top: 8px;
    padding: 13px;
    border: none;
    border-radius: 8px;

    color: #fff;
    font-size: 1.1rem;
    font-weight: 700;

    background: #AB2321;
    cursor: pointer;
}

.qr-btn:disabled {
    opacity: 0.6;
}

.qr-btn-outline {
    color: #AB2321;
    background: #fff;
    border: 1px solid #AB2321;
}

.qr-success {
    padding-top: 60px;
    text-align: center;
}

.qr-success-check {
    width: 74px;
    height: 74px;

    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 18px;

    border-radius: 50%;

    color: #fff;
    font-size: 2.4rem;

    background: #2e9e44;
}

.qr-success-title {
    font-size: 1.4rem;
    font-weight: 900;
}

.qr-success-comment {
    margin-top: 10px;
    font-size: 1.15rem;
    font-weight: 700;
}

.qr-success-hint {
    margin-top: 8px;
    color: #888;
    font-size: 0.9rem;
}
</style>
